import { useCallback, useEffect, useRef } from 'react';
import { EventBus, useNearbyDoor } from '@campus/core';
import { useIsTouchDevice } from '../hooks/useIsMobile.js';
import { Prompt } from '../ds/index.js';

/** Écart (px) entre le haut du sprite de la porte et le bas de l'invite. */
const PROMPT_GAP_PX = 8;

type Position = { x: number; y: number };

function placeAt(el: HTMLElement, pos: Position): void {
  el.style.transform = `translate3d(calc(${pos.x}px - 50%), calc(${pos.y}px - 100% - ${PROMPT_GAP_PX}px), 0)`;
}

/**
 * DoorPrompt — Invite d'approche affichée au-dessus de la porte à portée.
 *
 * Visibilité : gameStore.nearbyDoor (useNearbyDoor, écrit via door:nearby /
 * door:left par ZoneScene.checkDoors()). Position : door:prompt-position,
 * appliquée directement sur le DOM (hors rendu React) pour suivre la caméra
 * à chaque frame. Libellé : Toquer (bulle verrouillée), Entrer (salle liée),
 * sinon ouvrir. Sur tactile : pas de touche affichée, l'invite elle-même se
 * touche (remplace l'ancien bouton rond en bas à droite, caché sous le zoom).
 */
export function DoorPrompt() {
  const nearbyDoor = useNearbyDoor();
  const isTouch = useIsTouchDevice();
  const elRef = useRef<HTMLElement | null>(null);
  // Dernière position reçue, conservée même quand l'invite est démontée :
  // door:prompt-position est émis dans la même frame que door:nearby, avant
  // que React n'ait monté l'élément.
  const posRef = useRef<Position | null>(null);

  useEffect(() => {
    const onPosition = (pos: Position) => {
      posRef.current = pos;
      if (elRef.current) placeAt(elRef.current, pos);
    };
    EventBus.on('door:prompt-position', onPosition);
    return () => {
      EventBus.off('door:prompt-position', onPosition);
    };
  }, []);

  const setEl = useCallback((el: HTMLElement | null) => {
    elRef.current = el;
    if (el && posRef.current) placeAt(el, posRef.current);
  }, []);

  if (!nearbyDoor) return null;

  const label = nearbyDoor.locked
    ? 'Toquer'
    : nearbyDoor.linkedBubble ? 'Entrer' : 'Ouvrir';

  if (isTouch) {
    return (
      <button
        ref={setEl}
        type="button"
        aria-label={`${label} - porte`}
        onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); EventBus.emit('input:action-press', {}); }}
        onClick={() => EventBus.emit('input:action-press', {})}
        className="pl-door-prompt-touch"
        style={{ position: 'absolute', left: 0, top: 0, whiteSpace: 'nowrap', pointerEvents: 'auto', zIndex: 40 }}
      >
        <Prompt locked={nearbyDoor.locked}>{label}</Prompt>
      </button>
    );
  }

  return (
    <div
      ref={setEl}
      style={{ position: 'absolute', left: 0, top: 0, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 40 }}
    >
      <Prompt keyHint="Espace" locked={nearbyDoor.locked}>{label}</Prompt>
    </div>
  );
}
